import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Stack,
  Paper,
  TextField,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  AppBar,
  Button,
  IconButton,
  Toolbar,
  Typography,
  Tooltip,
  Checkbox,
} from '@mui/material';
import { CorrectAnswer } from '../room/game/CorrectAnswer';
import { CustomQuestionsHeader } from './CustomQuestionsHeader';
import { ScoreBar } from '../room/game/score-bar';
import { Player } from '../room/types';
import { StagedQuestionAnswer } from './staged-question-answer/QuestionAnswer';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateGame } from './game-loop';
import { fetchPost } from '../../api/common';
import InfoIcon from '@mui/icons-material/Info';
import { useSnackbar } from 'notistack';
import { QuestionSubmissionGuidelinesDialog } from '../../dialog/QuestionSubmissionGuidelinesDialog';
import { randomIntFromInterval, roundToOneDecimal } from '../room/utils';
import { CustomQuestionPreviewDialog } from '../../dialog/CustomQuestionPreviewDialog';
import PreviewIcon from '@mui/icons-material/Preview';
import GradingIcon from '@mui/icons-material/Grading';
import { Profile } from '@par/shared/types/player';

export const USER_FORM_CATEGORIES = [
  'Art & Architecture',
  'Business',
  'Classical Music',
  'Culture',
  'Film',
  'Food & Drink',
  'Geography',
  'History',
  'Literature',
  'Math',
  'Niche',
  'Philosophy & Religion',
  'Politics & Civics',
  'Popular Music',
  'Science',
  'Sports',
  'Theater',
  'TV',
  'Video Games',
  'Vocab & Language',
];

export const PAR_VALUES = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

export const GAME_STAGES = {
  COUNT_DOWN: 'COUNT_DOWN',
  DELAY_BEFORE_QUESTION_REVEAL: 'DELAY_BEFORE_QUESTION_REVEAL',
  REVEAL_QUESTION: 'REVEAL_QUESTION',
  REVEAL_BOXES: 'REVEAL_BOXES',
  DELAY_BEFORE_LETTER_REVEAL: 'DELAY_BEFORE_LETTER_REVEAL',
  PAUSED: 'PAUSED',
  REVEAL_ANSWER: 'REVEAL_ANSWER',
  WAIT_UNTIL_TIME_LIMIT: 'WAIT_UNTIL_TIME_LIMIT',
  NEXT_QUESTION: 'NEXT_QUESTION',
};

const REVEAL_TIME_LIMIT = 10;

export type CustomQuestionTestingData = {
  answerLetters: any[];
  countdown: number;
  delayBeforeQuestionRevealCount: number;
  delayBeforeLetterRevealCount: number;
  gameClock: number;
  gameId: string;
  gameOver: boolean;
  gameStage: string;
  gameStarted: boolean;
  players: Record<string, Player>;
  profile: Profile;
  questionIndex: number;
  questionMetadata: Record<string, any>;
  questionWordIndex: number;
  questionWords: string[];
  revealAnswer: string[][];
  revealBoxesCount: number;
  revealedLetters: string[][];
  revealModOffset: number;
  revealQuestion: string;
  revealTiming: number;
  roundOver: boolean;
  unrevealedIndex: any[];
  unrevealedProperNameIndex: [number, number];
};

type PropTypes = {
  profile?: Profile;
};

const create2dAnswerArray = (answerArray) => {
  const revealedLetters = [];
  let word = [];
  answerArray.forEach((letter) => {
    // if non-alphanumeric
    if (
      /[\W_]/.test(letter) &&
      Array.from(letter.normalize('NFD')).length === 1
    ) {
      if (letter === ' ') {
        revealedLetters.push(word);
        word = [];
      } else {
        word.push(letter);
      }
    } else {
      word.push(letter);
    }
  });
  revealedLetters.push(word);
  return revealedLetters;
};

const create2dRevealedLettersArray = (answerArray: string[]) => {
  const revealedLetters = [];
  let word = [];
  answerArray.forEach((letter) => {
    // if non-alphanumeric
    if (
      /[\W_]/.test(letter) &&
      Array.from(letter.normalize('NFD')).length === 1
    ) {
      if (letter === ' ') {
        revealedLetters.push(word);
        word = [];
      } else {
        if (letter === '-' || letter === '.') {
          word.push('');
        } else {
          word.push(letter);
        }
      }
    } else {
      word.push('');
    }
  });
  revealedLetters.push(word);
  return revealedLetters;
};

export const PROPER_NAME_SHORT_ANSWERS = [
  'none',
  'last word',
  'last two words',
  'first word',
  'first two words',
] as const;

type ProperNameShortAnswer = (typeof PROPER_NAME_SHORT_ANSWERS)[number];

export const CustomQuestions = ({ profile }: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [playerSelf, setPlayerSelf] = useState<Player>();
  const [question, setQuestion] = useState<string>();
  const [answer, setAnswer] = useState<string>();
  // const [answerArray, setAnswerArray] = useState<string>();
  const [answerSplit, setAnswerSplit] = useState<string[][]>();
  const [category, setCategory] = useState('');
  const [par, setPar] = useState('');
  const [alternateAnswers, setAlternateAnswers] = useState<string>('');
  const [promptAnswer, setPromptAnswer] = useState<string>('');
  const [properName, setProperName] = useState(false);
  const [properNameShortAnswer, setProperNameShortAnswer] =
    useState<ProperNameShortAnswer>('none');
  const [openGuidelines, setOpenGuidelines] = useState(false);
  const [openPreviewer, setOpenPreviewer] = useState(false);
  const testDataRef = useRef<CustomQuestionTestingData>();
  const [testData, setTestData] = useState<CustomQuestionTestingData>();
  const [authorComment, setAuthorComment] = useState<string>('');
  const [referenceLink, setReferenceLink] = useState<string>('');
  const secondsRef = useRef(0);
  const timerRef = useRef<any>();
  const formDirtyRef = useRef(false);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (formDirtyRef.current) {
      if (
        window.confirm('You made changed to this form. Do you want to go back?')
      ) {
        // your logic
        navigate(-1);
      } else {
        window.history.pushState(null, null, window.location.pathname);
      }
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    if (!profile || !answer) return;
    const initPlayerSelf: Player = {
      avatarPublicId: profile.avatarPublicId,
      avatarUrl: profile.avatarUrl,
      confirmedReady: false,
      correctAnswer: false,
      currentScore: 0,
      incorrectAnswer: false,
      fuzzyScore: null,
      isPromptAnswer: false,
      location: '',
      parScore: 0,
      profileId: profile.id,
      timerLimitReached: false,
      totalPenalty: 0,
      username: profile.username,
    };
    setPlayerSelf(initPlayerSelf);
    const answerLength = answer.replace(/\s/g, '').length;
    const answerWords = answer.toUpperCase().split(' ');
    const answerArray = answer.toUpperCase().split('');

    const selectUnrevealedProperNameIndex = randomIntFromInterval(
      0,
      answerWords[answerWords.length - 1].length
    );
    const answerLetters = create2dAnswerArray(answerArray);
    const categoryIsMath = false;
    const revealedLetters = create2dRevealedLettersArray(answerArray);
    const data: CustomQuestionTestingData = {
      answerLetters,
      countdown: 3,
      delayBeforeQuestionRevealCount: 0,
      delayBeforeLetterRevealCount: 0,
      gameClock: 0,
      gameId: 'game-id',
      gameOver: false,
      gameStage: GAME_STAGES.COUNT_DOWN,
      gameStarted: true,
      players: { [initPlayerSelf.profileId]: initPlayerSelf },
      profile,
      questionIndex: 0,
      questionMetadata: { category, par, questionSetCount: 1 },
      questionWordIndex: 0,
      questionWords: [],
      revealAnswer: null,
      revealBoxesCount: 0.5,
      revealedLetters: revealedLetters,
      revealModOffset: null,
      revealQuestion: '',
      revealTiming: roundToOneDecimal(REVEAL_TIME_LIMIT / answerLength),
      roundOver: false,
      unrevealedIndex: null,
      unrevealedProperNameIndex: [
        answerWords.length - 1,
        selectUnrevealedProperNameIndex,
      ],
    };
    testDataRef.current = data;
    setTestData(testDataRef.current);
  }, [profile, question, answer]);

  useEffect(() => {
    if (
      question ||
      answer ||
      category ||
      par ||
      alternateAnswers ||
      promptAnswer
    ) {
      formDirtyRef.current = true;
    } else {
      formDirtyRef.current = false;
    }
  }, [question, answer, category, par, alternateAnswers, promptAnswer]);

  const submitQuestion = async () => {
    if (!answer || !category || !question || !par) {
      enqueueSnackbar(
        `You must provide a question, answer, category, and par`,
        {
          variant: 'warning',
        }
      );
      return;
    }
    const body = {
      customQuestionProps: {
        answer,
        authorComment,
        category,
        question,
        par,
        alternateAnswers: alternateAnswers.split(',').map((a) => a.trim()),
        promptAnswer: promptAnswer.split(',').map((a) => a.trim()),
        properName,
        properNameShortAnswer,
        referenceLink,
      },
      profileId: profile.id,
    };
    try {
      const res = await fetchPost('/custom-questions/submit-question', body);
      enqueueSnackbar(`Question submitted for review`, { variant: 'success' });
      clearFormClick();
    } catch {
      enqueueSnackbar(`There was an issue with the submission`, {
        variant: 'warning',
      });
    }
  };

  const clearFormClick = async () => {
    setQuestion('');
    setAnswer('');
    setAnswerSplit(undefined);
    setCategory('');
    setPar('');
    setAlternateAnswers('');
    setPromptAnswer('');
    setProperName(false);
    setProperNameShortAnswer('none');
    setAuthorComment('');
    setReferenceLink('');
  };

  const scoreBars = (playerSelf: Player) => (
    <ScoreBar
      avatar={playerSelf.avatarUrl}
      mode={'match'}
      score={playerSelf.parScore || 0}
      username={playerSelf.username || 'Player 1'}
      usernameLocation={'left'}
    ></ScoreBar>
  );

  const handleQuestion = (value: string) => setQuestion(value);
  const handleAnswer = (value: string) => {
    setAnswer(value);
    const answerSplit: string[][] = [];
    const splitWord = value.split(' ');
    splitWord.forEach((w) => {
      answerSplit.push(w.split(''));
    });
    answerSplit.forEach((w, i) => {
      w.forEach((l, j) => {
        if (l === '~') {
          answerSplit[i][j] = '';
        }
      });
    });
    setAnswerSplit(answerSplit);
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value);
  };

  const handleParChange = (event: SelectChangeEvent) => {
    setPar(event.target.value);
  };

  const handleAlternateAnswers = (value: string) => {
    setAlternateAnswers(value);
  };

  const handlePromptAnswers = (value: string) => {
    setPromptAnswer(value);
  };

  const handleProperNameShortAnswerChange = (event: SelectChangeEvent) => {
    const value = event.target.value as ProperNameShortAnswer;
    setProperNameShortAnswer(value);
    if (value !== 'none') {
      setProperName(true);
    }
  };

  const handleAuthorComment = (text: string) => {
    if (text.length <= 200) {
      setAuthorComment(text);
    }
  };

  const handlePreviewClick = () => {
    if (!question) return;
    // clearTimeout(timerRef.current);
    resetTestData();
    setOpenPreviewer(true);
    startTimer();
  };

  const resetTestData = () => {
    const answerLength = answer.replace(/\s/g, '').length;
    const answerWords = answer.toUpperCase().split(' ');
    const answerArray = answer.toUpperCase().split('');

    const selectUnrevealedProperNameIndex = randomIntFromInterval(
      0,
      answerWords[answerWords.length - 1].length
    );
    const answerLetters = create2dAnswerArray(answerArray);
    const categoryIsMath = false;
    const revealedLetters = create2dRevealedLettersArray(answerArray);
    const data: CustomQuestionTestingData = {
      answerLetters,
      countdown: 3,
      delayBeforeQuestionRevealCount: 0,
      delayBeforeLetterRevealCount: 0,
      gameClock: 0,
      gameId: 'game-id',
      gameOver: false,
      gameStage: GAME_STAGES.COUNT_DOWN,
      gameStarted: true,
      players: { [playerSelf.profileId]: playerSelf },
      profile,
      questionIndex: 0,
      questionMetadata: { category, par, questionSetCount: 1 },
      questionWordIndex: 0,
      questionWords: question?.split(' '),
      revealAnswer: null,
      revealBoxesCount: 0.5,
      revealedLetters: revealedLetters,
      revealModOffset: null,
      revealQuestion: '',
      revealTiming: roundToOneDecimal(REVEAL_TIME_LIMIT / answerLength),
      roundOver: false,
      unrevealedIndex: null,
      unrevealedProperNameIndex: [
        answerWords.length - 1,
        selectUnrevealedProperNameIndex,
      ],
    };
    testDataRef.current = data;
    setTestData(testDataRef.current);
  };

  const startTimer = () => {
    timerRef.current = setTimeout(() => {
      const newData = updateGame(secondsRef.current, testDataRef.current);
      testDataRef.current = { ...newData };
      setTestData(testDataRef.current);
      secondsRef.current = secondsRef.current + 0.1;
      startTimer();
    }, 100);
  };

  const handleClosePreviewer = () => {
    setOpenPreviewer(false);
    clearTimeout(timerRef.current);
  };

  return (
    <Container sx={{ paddingBottom: '100px' }}>
      <CustomQuestionPreviewDialog
        close={handleClosePreviewer}
        // customQuestionTestingData={testDataRef.current}
        customQuestionTestingData={testData}
        open={openPreviewer}
        profile={profile}
      />
      <QuestionSubmissionGuidelinesDialog
        close={() => setOpenGuidelines(false)}
        open={openGuidelines}
      />
      <Typography variant="h4" textAlign={'center'} letterSpacing={2} mb={2}>
        Custom Questions
      </Typography>
      <Box sx={{ flexGrow: 1, marginBottom: 2 }}>
        <AppBar position="static">
          <Toolbar>
            <Stack
              width={'50%'}
              direction="row"
              spacing={2}
              sx={{ display: 'flex', justifyContent: 'left' }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenGuidelines(true)}
                startIcon={<GradingIcon />}
              >
                Submission guidelines
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePreviewClick}
                startIcon={<PreviewIcon />}
              >
                Preview in game (WIP)
              </Button>
            </Stack>
            <Stack
              width={'50%'}
              direction="row"
              spacing={2}
              sx={{ display: 'flex', justifyContent: 'right' }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={clearFormClick}
              >
                Clear Form
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={submitQuestion}
              >
                Submit for review
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>
      </Box>
      <Stack
        spacing={4}
        mb={5}
        sx={{
          padding: 2,
          border: '1px solid',
          borderColor: theme.palette.primary.main,
          borderRadius: '10px',
        }}
      >
        <Stack direction={'row'} spacing={2} mt={2}>
          <Stack
            sx={{
              width: '75%',
            }}
          >
            <TextField
              id="question"
              label="Question"
              variant="standard"
              value={question}
              multiline
              onChange={(e) => handleQuestion(e.target.value)}
            />
            <Typography variant="caption">
              Word count: {question?.split(' ').length || 0}
            </Typography>
          </Stack>
          <TextField
            id="answer"
            label="Answer"
            variant="standard"
            sx={{
              width: '25%',
            }}
            value={answer}
            onChange={(e) => handleAnswer(e.target.value)}
          />
        </Stack>
        <Stack
          direction={'row'}
          spacing={4}
          mt={2}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={handleCategoryChange}
            >
              <MenuItem disabled value="">
                Select
              </MenuItem>
              {USER_FORM_CATEGORIES.map((c) => (
                <MenuItem value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>PAR</InputLabel>
            <Select value={par} label="Par" onChange={handleParChange}>
              <MenuItem disabled value="">
                Select
              </MenuItem>
              {PAR_VALUES.map((c) => (
                <MenuItem value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row">
            <Box
              position={'relative'}
              sx={{
                marginRight: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Tooltip
                title={
                  'Use this to denote common names where just the last name is acceptable ("Aaron" for "Hank Aaron"). Some names require specific combinations, e.g., "last two words" for "van gogh"'
                }
                placement="top"
              >
                <InfoIcon sx={{ color: 'lightgray' }} />
              </Tooltip>
            </Box>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Special answer allowance</InputLabel>
              <Select
                value={properNameShortAnswer}
                label="Special answer allowance"
                onChange={handleProperNameShortAnswerChange}
              >
                {PROPER_NAME_SHORT_ANSWERS.map((c) => (
                  <MenuItem value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
        <Stack direction={'row'} spacing={2}>
          <TextField
            id="alternate-answers"
            label="Alternate answers (separate answers with a comma)"
            variant="standard"
            sx={{
              width: '100%',
            }}
            value={alternateAnswers}
            onChange={(e) => handleAlternateAnswers(e.target.value)}
          />
          <TextField
            id="prompt-answers"
            label="Prompt keywords (separate prompts with a comma)"
            variant="standard"
            sx={{
              width: '100%',
            }}
            value={promptAnswer}
            onChange={(e) => handlePromptAnswers(e.target.value)}
          />
        </Stack>
        <Stack direction={'row'} spacing={2}>
          <Stack
            sx={{
              width: '70%',
            }}
          >
            <TextField
              label="Leave a comment for the players (optional)"
              variant="standard"
              value={authorComment}
              onChange={(e) => handleAuthorComment(e.target.value)}
            />
            <Typography variant="caption">
              Char count: {authorComment.length}
            </Typography>
          </Stack>
          <TextField
            label="Reference link (optional)"
            variant="standard"
            sx={{
              width: '30%',
            }}
            value={referenceLink}
            onChange={(e) => setReferenceLink(e.target.value)}
          />
        </Stack>
      </Stack>
      {playerSelf ? (
        <>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: theme.palette.background.default + 'e6',
            }}
          >
            {scoreBars(playerSelf)}
            <Stack spacing={1} marginTop={1}>
              <CustomQuestionsHeader
                category={category || 'Category'}
                gameClock={10}
                mode={'solo'}
                par={+par}
                playerSelf={playerSelf}
                questionIndex={2}
                totalNumQuestions={10}
              />
              <div style={{ height: '24px' }}></div>
            </Stack>
          </Box>
          <Box sx={{ height: '300px' }}>
            <StagedQuestionAnswer
              answer={
                answerSplit || [
                  ['A', 'N', 'S'],
                  ['W', 'E', 'R'],
                ]
              }
              question={question || 'question here'}
              timerLimitReached={false}
              unrevealedIndex={[1]}
              correctAnswer={playerSelf.correctAnswer}
              properNameShortAnswer={undefined}
            ></StagedQuestionAnswer>
          </Box>
        </>
      ) : null}
    </Container>
  );
};
